
import { Badge } from "@/components/ui/badge";
import { DemographicSegment } from "./types";

interface DemographicInsightsProps {
  segments: DemographicSegment[];
  colors: string[];
}

// Helper functions for insights text
function findHighestSentiment(segments: DemographicSegment[]) {
  return segments.reduce((prev, current) => 
    (prev.agreePercentage ?? 0) > (current.agreePercentage ?? 0) ? prev : current
  ).name;
}

function findHighestSentimentValue(segments: DemographicSegment[]) {
  return segments.reduce((prev, current) => 
    Math.max(prev, current.agreePercentage ?? 0)
  , 0);
}

function findLowestSentiment(segments: DemographicSegment[]) {
  return segments.reduce((prev, current) => 
    (prev.agreePercentage ?? 100) < (current.agreePercentage ?? 100) ? prev : current
  ).name;
}

function findLowestSentimentValue(segments: DemographicSegment[]) {
  return segments.reduce((prev, current) => 
    Math.min(prev, current.agreePercentage ?? 100)
  , 100);
}

const DemographicInsights = ({ segments, colors }: DemographicInsightsProps) => {
  if (!segments.length) return null;
  
  return (
    <div className="mt-4 max-w-3xl">
      <h3 className="text-sm font-medium mb-2">Key Insights:</h3>
      <ul className="space-y-2">
        {segments.map((segment, index) => (
          <li key={index} className="flex items-center">
            <span 
              className="w-3 h-3 inline-block mr-2 rounded-full" 
              style={{ backgroundColor: colors[index % colors.length] }}
            ></span>
            <span>
              <Badge variant="outline" className="mr-2">{segment.name}</Badge>
              {segment.percentage}% of respondents 
              {segment.agreePercentage !== undefined && (
                <span className="ml-1">with {segment.agreePercentage}% sentiment score</span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {segments.length > 1 && (
        <p className="mt-4 text-sm text-muted-foreground">
          The data shows {findHighestSentiment(segments)} has the highest sentiment score at 
          {" " + findHighestSentimentValue(segments)}% while {findLowestSentiment(segments)} has the lowest at
          {" " + findLowestSentimentValue(segments)}%.
        </p>
      )}
    </div>
  );
};

export default DemographicInsights;
